/**
 * LiftCloud refuses a role create or update that would hand out permissions
 * the caller does not hold with a 403 whose body names each one:
 *
 *   {"status": "error", "message": "...",
 *    "missing_permissions": [{"key": "servers-delete", "name": "servers", "description": "Delete servers."}]}
 *
 * `name` is the page the key sits on in the roles editor. These helpers turn
 * that into the text the save toast shows, so an administrator can see which
 * permissions to remove (or ask for) instead of a bare "beyond your own".
 */

/** One permission the caller lacks, as LiftCloud sends it. */
export interface MissingPermission {
  key: string;
  name?: string;
  description?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** The missing permissions a failed role save names, or [] when it names none. */
export function missingPermissionsOf(error: unknown): MissingPermission[] {
  if (!isRecord(error) || !isRecord(error.data)) {
    return [];
  }

  const missing = error.data.missing_permissions;
  if (!Array.isArray(missing)) {
    return [];
  }

  return missing.flatMap((entry): MissingPermission[] => {
    if (!isRecord(entry) || typeof entry.key !== "string" || entry.key === "") {
      return [];
    }

    return [
      {
        key: entry.key,
        name: typeof entry.name === "string" ? entry.name : undefined,
        description: typeof entry.description === "string" ? entry.description : undefined,
      },
    ];
  });
}

/** "servers-delete (servers)", or the bare key when the page is unnamed. */
function describeMissingPermission({ key, name }: MissingPermission): string {
  return name ? `${key} (${name})` : key;
}

/**
 * The save error with its message extended by the permissions it names, for
 * GlobalAlert2 to show. An error that names none is returned unchanged.
 */
export function withMissingPermissions<E>(error: E, label: string): E {
  const missing = missingPermissionsOf(error);
  if (missing.length === 0 || !isRecord(error) || !isRecord(error.data)) {
    return error;
  }

  const { message } = error.data;
  const listed = `${label}: ${missing.map(describeMissingPermission).join(", ")}`;

  return {
    ...error,
    data: {
      ...error.data,
      message: typeof message === "string" && message !== "" ? `${message}. ${listed}` : listed,
    },
  };
}
